import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import WorkflowFlowPreview from "./components/WorkflowFlowPreview";
import { useGetWorkflowConfigrationQuery } from "store/slices/apis/workflowConfigrationSlice";
import { useGetAllRolesQuery } from "store/slices/apis/rolesSlice";

export default function WorkflowFlowView() {
  const navigate = useNavigate();
  const { projectId, id } = useParams();

  const {
    data: workflowResponse,
    isLoading: isWorkflowLoading,
    error: workflowError,
  } = useGetWorkflowConfigrationQuery(id);

  const workflow = workflowResponse?.data;

  const {
    data: rolesResponse,
    isLoading: isRolesLoading,
  } = useGetAllRolesQuery(
    { projectId: workflow?.projectId || projectId },
    { skip: !(workflow?.projectId || projectId) }
  );

  const roles = useMemo(() => {
    const list = rolesResponse?.data ?? rolesResponse ?? [];
    return list.map((role) => ({
      value: role.id ?? role.value,
      label: role.displayName ?? role.label,
    }));
  }, [rolesResponse]);

  const totalSteps = Array.isArray(workflow?.steps) ? workflow.steps.length : 0;
  const loading = isWorkflowLoading || (Boolean(workflow?.projectId || projectId) && isRolesLoading);

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (workflowError || !workflow) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ mb: 2, fontFamily: "Outfit, sans-serif", fontWeight: 600 }}>
          Unable to load workflow flowchart.
        </Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "16px", sm: "18px", md: "18px" },
                  lineHeight: "24px",
                  color: "#252C32",
                  fontFamily: "Outfit, sans-serif",
                }}
              >
                Config & Settings
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "18px", sm: "32px", md: "36px" },
                  lineHeight: { xs: "28px", md: "48px" },
                  color: "#252C32",
                  fontFamily: "Outfit, sans-serif",
                }}
              >
                Workflow Flowchart
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={() =>
                  navigate(`/projects/${workflow.projectId || projectId}/settings/workflow/${workflow.workflowConfigurationId}/edit`)
                }
              >
                Edit Workflow
              </Button>
            </Stack>
          </Box>
        </Grid>

        <Grid size={12}>
          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  sx={{
                    fontSize: "28px",
                    fontWeight: 700,
                    color: "#252C32",
                    fontFamily: "Outfit, sans-serif",
                    mb: 1,
                  }}
                >
                  {workflow.workflowName || "Untitled Workflow"}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                  <Chip
                    label={workflow.entityName || "No entity selected"}
                    sx={{
                      bgcolor: "#EEF4FF",
                      color: "#0A65E4",
                      fontFamily: "Outfit, sans-serif",
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={`${totalSteps} ${totalSteps === 1 ? "Step" : "Steps"}`}
                    sx={{
                      bgcolor: "#F4F7FB",
                      color: "#5F6B84",
                      fontFamily: "Outfit, sans-serif",
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={workflow.isActive ? "Active" : "Inactive"}
                    color={workflow.isActive ? "success" : "default"}
                    sx={{
                      fontFamily: "Outfit, sans-serif",
                      fontWeight: 600,
                    }}
                  />
                </Stack>
              </Box>

              {workflow.description ? (
                <Typography
                  sx={{
                    fontSize: "15px",
                    lineHeight: "24px",
                    color: "#5F6B84",
                    fontFamily: "Outfit, sans-serif",
                  }}
                >
                  {workflow.description}
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        </Grid>

        <Grid size={12}>
          <Formik initialValues={workflow} onSubmit={() => {}}>
            <Form>
              <WorkflowFlowPreview roles={roles} />
            </Form>
          </Formik>
        </Grid>
      </Grid>
    </Box>
  );
}
